import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  PDKOpenNewTabIcon,
  PDKToastInfoBadgeIcon,
} from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useGoToDeal from '@/hooks/use-go-to-deal';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import type { Trade } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { Link } from 'lucide-react';
import UserProfileCard from '../deal/activity-feed/user-profile-card';
import { TradeDealSelector } from './deal/trade-deal-selector';
import TradeAuctionsEditor from './location/trade-auctions-editor';
import { TradeStatusSelector } from './status/trade-status-selector';

const clipboardDealLink = () => {
  navigator.clipboard.writeText(window.location.href);
  toast({
    description: (
      <div className="flex flex-row gap-2 items-center">
        <PDKToastInfoBadgeIcon className="size-4" />
        <p>Deal link copied to clipboard</p>
      </div>
    ),
  });
};

// const clipboardCDKId = (cdk_id: number | null) => {
//   if (!cdk_id) {
//     toast({
//       description: (
//         <div className="flex flex-row gap-2 items-center">
//           <PDKToastInfoBadgeIcon className="size-4" />
//           <p>No CDK ID found</p>
//         </div>
//       ),
//     });
//     return;
//   }
//   navigator.clipboard.writeText(cdk_id.toString());
//   toast({
//     description: (
//       <div className="flex flex-row gap-2 items-center">
//         <PDKToastInfoBadgeIcon className="size-4" />
//         <p>CDK ID copied to clipboard</p>
//       </div>
//     ),
//   });
// };

// const clipboardVIN = (vin: string) => {
//   navigator.clipboard.writeText(vin);
//   toast({
//     description: (
//       <div className="flex flex-row gap-2 items-center">
//         <PDKToastInfoBadgeIcon className="size-4" />
//         <p>VIN copied to clipboard</p>
//       </div>
//     ),
//   });
// };

const LEFT_PADDING = 'px-2';

const NonMobileAside = ({ trade }: { trade: Trade }) => {
  const createdByUser = trade.createdBy;

  const deal = trade.deal_id ? dealStore.getById(trade.deal_id) : null;

  const { goToDeal } = useGoToDeal();

  return (
    <div className="flex flex-col dark:bg-dark">
      <header className="flex flex-row border-b">
        <div className="flex flex-row w-full h-10 shrink-0 items-center justify-between p-4 gap-2">
          <div className="text-xs text-gray-500">Properties</div>
          <div className="flex flex-row gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                  onClick={clipboardDealLink}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                Copy trade link{' '}
                <KeyboardKeyIcon>{HOTKEYS.DEAL_CLIPBOARD_LINK}</KeyboardKeyIcon>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>

      <div className="flex flex-col p-2 gap-4 h-[calc(100vh-40px)] overflow-auto ml-1">
        {/* <div className="flex flex-col">
          
          <div className="text-xs text-gray-500 py-2 px-1">
            Risk
          </div>
          <div className="flex flex-row gap-3 items-center">
            {deal.risk == null ? (
              <Button variant="ghost" className="p-1 h-fit">
                <FileQuestion
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                />
                <p className="rounded text-xs  text-gray-600">
                  No risk assessment
                </p>
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" className="p-1 h-fit">
                    {deal.risk.level == 'low' ? (
                      <>
                        <ShieldCheck
                          className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                        />
                        <p className="rounded text-xs  text-gray-600">
                          Low Risk
                        </p>
                      </>
                    ) : deal.risk.level == 'needs_attention' ? (
                      <>
                        <ShieldAlert
                          className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                        />
                        <p className="rounded text-xs  text-gray-600">
                          Attention Required
                        </p>
                      </>
                    ) : (
                      <>
                        <ShieldAlert
                          className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                        />
                        <p className="rounded text-xs  text-gray-600">
                          High Risk
                        </p>
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {deal.risk.level == 'low' ? (
                    <p>Risk of fraud is low, but never zero! :D</p>
                  ) : deal.risk.level == 'needs_attention' ? (
                    <>
                      <p>
                        Please ask a manager to review. Risk of fraud is not
                        high, but not low either.
                      </p>
                      <p className="">Checks failed</p>
                      <ul className="list-disc list-inside">
                        {deal.risk.checks_failed.map((check) => (
                          <li key={check}>{check}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      <p>Most likely fraud. Please ask a manager to review.</p>
                      <p className="">Checks failed</p>
                      <ul className="list-disc list-inside">
                        {deal.risk.checks_failed.map((check) => (
                          <li key={check}>{check}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div> */}

        <div className="flex flex-row gap-3 items-center">
          <TradeStatusSelector
            id={trade.id}
            value={trade.status}
            withHotkeyListener
            className={LEFT_PADDING}
          />
        </div>

        <div className="flex flex-col">
          <div className="flex flex-row gap-1 items-center">
            <div className="text-xs text-gray-500 py-2 px-1">Deal</div>
            {trade.deal_id && (
              <div
                onClick={() => {
                  goToDeal({
                    dealShortId: deal?.short_id || '',
                    isForInbox: false,
                    queryParams: {},
                    openInNewTab: true,
                  });
                }}
              >
                <PDKOpenNewTabIcon className="cursor-pointer size-3.5 text-gray-500" />
              </div>
            )}
          </div>
          <TradeDealSelector
            id={trade.id}
            dealId={trade.deal_id || ''}
            withHotkeyListener
            // className={LEFT_PADDING}
          />
        </div>

        {createdByUser && (
          <div className="flex flex-col">
            <div className="text-xs text-gray-500 py-2 px-1">Created By</div>
            <div className="flex flex-row gap-3 items-center">
              {createdByUser && (
                <UserProfileCard user={createdByUser} withAvatar />
              )}
            </div>
          </div>
        )}

        <TradeAuctionsEditor trade={trade} />
      </div>
    </div>
  );
};

// const ContextMenuWrapper = (Component: typeof NonMobileAside) => {
//   return ({ deal, dealShortId }: { deal: Deal; dealShortId: string }) => {
//     const { toggleFavorite } = useFavoriteMutation(deal);

//     return (
//       <ContextMenu>
//         <ContextMenuTrigger>
//           <Component deal={deal} dealShortId={dealShortId} />
//         </ContextMenuTrigger>
//         <ContextMenuContent className="w-52">
//           <ContextMenuItem onClick={toggleFavorite} className="text-xs">
//             <Star
//               className={cn('h-3.5 w-3.5 cursor-pointer', {
//                 'fill-yellow-500': deal.favorited,
//               })}
//             />
//             {deal.favorited ? 'Unfavorite' : 'Favorite'}
//           </ContextMenuItem>
//           <ContextMenuSub>
//             <ContextMenuSubTrigger className="gap-2 text-xs">
//               <Copy className="size-3.5" />
//               Copy
//             </ContextMenuSubTrigger>
//             <ContextMenuSubContent className="w-60 text-xs">
//               <ContextMenuItem onClick={clipboardDealLink} className="text-xs">
//                 <Link className="size-3.5" />
//                 Copy URL
//                 {/* command shift l */}
//                 <ContextMenuShortcut>
//                   <KeyboardKeyIcon>
//                     {HOTKEYS.DEAL_CLIPBOARD_LINK}
//                   </KeyboardKeyIcon>
//                 </ContextMenuShortcut>
//               </ContextMenuItem>
//               <ContextMenuItem
//                 onClick={() => clipboardCDKId(deal.cdk_id)}
//                 className="text-xs"
//                 disabled={!deal.cdk_id}
//               >
//                 <PDKCDKIcon className="border-none p-0" />
//                 {deal.cdk_id ? 'Copy CDK ID' : 'No CDK ID'}
//                 {/* command shift C */}
//                 <ContextMenuShortcut>
//                   <KeyboardKeyIcon>
//                     {HOTKEYS.DEAL_CLIPBOARD_CDK_ID}
//                   </KeyboardKeyIcon>
//                 </ContextMenuShortcut>
//               </ContextMenuItem>
//               <ContextMenuItem
//                 onClick={() => clipboardVIN(deal.vehicle.vin)}
//                 className="text-xs"
//               >
//                 <PDKBarcodeIcon className="size-3.5" />
//                 Copy VIN
//                 {/* command shift I */}
//                 <ContextMenuShortcut>
//                   <KeyboardKeyIcon>
//                     {HOTKEYS.DEAL_CLIPBOARD_VIN}
//                   </KeyboardKeyIcon>
//                 </ContextMenuShortcut>
//               </ContextMenuItem>
//             </ContextMenuSubContent>
//           </ContextMenuSub>
//         </ContextMenuContent>
//       </ContextMenu>
//     );
//   };
// };

// export default ContextMenuWrapper(NonMobileAside);

export default NonMobileAside;
